import { ChevronFirstIcon, ChevronLastIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/**
 * The one pager: "41–60 of 312" (or "3 / 16" without a total) and prev/next,
 * plus first/last when `onFirst`/`onLast` are given. Event-based, so it drives
 * a TanStack table, a Clerk paginated resource or a server-paged list alike.
 *
 * @example
 * import { useState } from "react"
 * import { PageNav } from "@/components/ui/page-nav"
 *
 * export function Pager({ total, pageSize = 20 }: { total: number; pageSize?: number }) {
 *   const [page, setPage] = useState(1)
 *   const pageCount = Math.max(1, Math.ceil(total / pageSize))
 *   return (
 *     <PageNav
 *       page={page}
 *       pageCount={pageCount}
 *       total={total}
 *       pageSize={pageSize}
 *       onPrevious={() => setPage((p) => Math.max(1, p - 1))}
 *       onNext={() => setPage((p) => Math.min(pageCount, p + 1))}
 *     />
 *   )
 * }
 */
function PageNav({
  page,
  pageCount,
  total,
  pageSize,
  onPrevious,
  onNext,
  onFirst,
  onLast,
  disabled,
  className,
}: {
  /** 1-based. */
  page: number;
  pageCount: number;
  total?: number;
  pageSize?: number;
  onPrevious: () => void;
  onNext: () => void;
  onFirst?: () => void;
  onLast?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  const canPrevious = !disabled && page > 1;
  const canNext = !disabled && page < pageCount;
  const range =
    total != null && pageSize != null
      ? `${total === 0 ? 0 : (page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`
      : `${page} / ${Math.max(pageCount, 1)}`;

  return (
    <div
      data-slot="page-nav"
      className={cn("flex items-center justify-between gap-2 text-xs text-muted-foreground", className)}
    >
      <span className="font-mono tabular-nums" aria-live="polite">
        {range}
      </span>
      <div className="flex items-center gap-1">
        {onFirst && (
          <Button variant="outline" size="icon-sm" scope={false} onClick={onFirst} disabled={!canPrevious} aria-label="First page">
            <ChevronFirstIcon />
          </Button>
        )}
        <Button variant="outline" size="icon-sm" scope={false} onClick={onPrevious} disabled={!canPrevious} aria-label="Previous page">
          <ChevronLeftIcon />
        </Button>
        <Button variant="outline" size="icon-sm" scope={false} onClick={onNext} disabled={!canNext} aria-label="Next page">
          <ChevronRightIcon />
        </Button>
        {onLast && (
          <Button variant="outline" size="icon-sm" scope={false} onClick={onLast} disabled={!canNext} aria-label="Last page">
            <ChevronLastIcon />
          </Button>
        )}
      </div>
    </div>
  );
}

export { PageNav };
